/**
 * URL grammar for archive routes (the segments AFTER /{locale}/{archive}).
 *
 *   []                                      → index, page 1
 *   ["page", "{n}"]                         → index, page N
 *   ["{kind}", "{slug}"]                    → filtered (kind = "category" | "tag"), page 1
 *   ["{kind}", "{slug}", "page", "{n}"]     → filtered, page N
 *   ["{slug}"]                              → single article
 *
 * Anything else returns null — caller decides whether to 404 or fall through.
 *
 * Pure function: no Nuxt/runtime imports. Both the page dispatcher
 * (`app/pages/[locale]/[...slug].vue`) and the locale-switch resolver
 * (`app/utils/categoryI18n.ts`) consume this so the grammar lives in one place.
 */
export type ArchiveRouteShape =
  | { kind: 'index'; page: number }
  | { kind: 'filtered'; filterKind: 'category' | 'tag'; filterSlug: string; page: number }
  | { kind: 'article'; slug: string }

export function parseArchiveSubpath(rest: string[]): ArchiveRouteShape | null {
  if (rest.length === 0) return { kind: 'index', page: 1 }

  // Reserved keywords (page/category/tag) at position 0 enter their own
  // branches and only match the exact lengths documented above. A bare
  // `["page"]`, `["category"]`, or `["tag"]` falls through to `null` —
  // a behavior tightening over the previous inline parser, which would
  // have treated `["category"]` as an article slug.
  if (rest[0] === 'page') {
    if (rest.length === 2) {
      return { kind: 'index', page: parseInt(rest[1]!, 10) || 1 }
    }
    return null
  }

  if (rest[0] === 'category' || rest[0] === 'tag') {
    const filterKind = rest[0]
    const filterSlug = rest[1]
    if (!filterSlug) return null
    if (rest.length === 2) {
      return { kind: 'filtered', filterKind, filterSlug, page: 1 }
    }
    if (rest.length === 4 && rest[2] === 'page') {
      return { kind: 'filtered', filterKind, filterSlug, page: parseInt(rest[3]!, 10) || 1 }
    }
    return null
  }

  if (rest.length === 1) {
    return { kind: 'article', slug: rest[0]! }
  }

  return null
}
